/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { InputType, IFormItem } from '../bju-onboarding-form';                                  // @CUSTOM: Import IFormItem for radio button boolean values
import { IBjuOnboardingFormResources } from '../bju-onboarding-form.props.autogenerated';       // @CUSTOM: Import IBjuOnboardingFormResources for radio button boolean labels

export interface IBusinessSignUpInputProps {
    id?: string;
    type: InputType;
    value?: string;
    pattern?: string;
    className: string;
    maxLength?: string;
    isRequired: boolean;
    fieldReference?: React.RefObject<HTMLInputElement>;
    onChange?(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void;
    resources: IBjuOnboardingFormResources;     // @CUSTOM: Pass resources for radio button boolean labels
    formItems: IFormItem[];                     // @CUSTOM: Pass state form data for radio button boolean checked values
}

export interface IBusinessSignUpInput {
    key: string;
    AddressItem: INodeProps;
    label: React.ReactNode;
    alert: React.ReactNode;
    input: React.ReactNode;
}

const GetMaxLength = (maxLength?: string): number | undefined => {
    if (maxLength) {
        const parsedMaxLength = Number.parseInt(maxLength, 10);
        if (!isNaN(parsedMaxLength)) {
            return parsedMaxLength;
        }
    }

    return undefined;
};

export const SignUpInput: React.FC<IBusinessSignUpInputProps> = ({
    id,
    type,
    value,
    pattern,
    className,
    maxLength,
    isRequired,
    onChange,
    fieldReference,
    resources,  // @CUSTOM: Pass resources for radio button boolean labels
    formItems   // @CUSTOM: Pass state form data for radio button boolean checked values
}) => {
    if (type === InputType.TextArea) {
        return (
            <textarea
                id={id}
                name={id}
                value={value}
                className={`${className}-input-area ${className}-${id}`}
                aria-required={isRequired}
                maxLength={GetMaxLength(maxLength)}
                onChange={onChange}
            />
        );
    }

    //------------------------------------------------------
    // @CUSTOM: Boolean input type
    //------------------------------------------------------
    if (type === InputType.Boolean) {
        const inputValue = formItems.find(item => item.id === id)?.value;
        return (
            <div className={`${className}-radio ${className}-${id}`}>
                <label className={`${className}-radio-label`}>
                    <input
                        id={id}
                        className={`${className}-radio-input`}
                        type='radio'
                        value={resources.booleanTrueLabel}
                        checked={inputValue === resources.booleanTrueLabel && true}
                        onChange={onChange}
                    />
                    {resources.booleanTrueLabel}
                </label>
                <label className={`${className}-radio-label`}>
                    <input
                        id={id}
                        className={`${className}-radio-input`}
                        type='radio'
                        value={resources.booleanFalseLabel}
                        checked={inputValue === resources.booleanFalseLabel && true}
                        onChange={onChange}
                    />
                    {resources.booleanFalseLabel}
                </label>
            </div>
        );
    }
    //------------------------------------------------------

    return (
        <input
            id={id}
            type={type}
            value={value}
            className={`${className}-input ${className}-${id}`}
            pattern={pattern !== '' ? pattern : undefined}
            aria-required={isRequired}
            maxLength={GetMaxLength(maxLength)}
            onChange={onChange}
            ref={fieldReference}
        />
    );
};

export default SignUpInput;
